import { Component, h, Host, Prop, State } from '@stencil/core';
import { WindowResizeService } from '../../services/window-resize-service';
import { FLEX_JUSTIFY_OPTIONS, GRID_TYPES, ResponsiveBoolean, ResponsiveNumber, ResponsiveString } from '../../typings';
import { Element, HTMLStencilElement } from '@stencil/core/internal';

/**
 * @slot media - This slot can either be a video or image component.
 * @slot content - This slot will usually serve a content text block.
 */
@Component({
  tag: 'content-grid-formatter',
  styleUrl: 'content-grid-formatter.scss',
  shadow: false,
})
export class ContentGridFormatter {
  /**
   * @type String
   * @responsive
   */
  @Prop() classes: ResponsiveString;
  @State() classString;
  /**
   * Flex or grid
   * @type Select
   * @options none
   * @options flex
   * @options grid
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  type: GRID_TYPES | ResponsiveString = 'flex';
  /**
   * Setting this true to will grow all children. Alternatively, add the attribute [flex-grow="true"] to a specific grid child and that child will grow.
   * @type Boolean
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  flexGrow: ResponsiveBoolean = 'false';
  /**
   * Setting this true to will shrink all children. Alternatively, add the attribute [flex-shrink="true"] to a specific grid child and that child will shrink.
   * @type Boolean
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  flexShrink: ResponsiveBoolean = 'false';
  /**
   * Vertical axis alignment
   * @type Select
   * @options flex-start
   * @options space-between
   * @options center
   * @options space-evenly
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  vertical: FLEX_JUSTIFY_OPTIONS = 'space-between';

  /**
   * Horizontal axis alignment
   * @type Select
   * @options flex-start
   * @options space-between
   * @options center
   * @options space-evenly
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  horizontal: FLEX_JUSTIFY_OPTIONS = 'space-between';
  /**
   * @type Number
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  columns: ResponsiveNumber = '1';

  /**
   * @type Number
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  columnGap: ResponsiveNumber = '0';

  /**
   * @type Number
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  rowGap: ResponsiveNumber = '0';

  @Element() hostElement: HTMLStencilElement;
  @State() activeClassString: string;
  @State() inlineStyle: { [key: string]: string } = {};

  componentWillLoad() {
    return Promise.allSettled([
      WindowResizeService.registerProp(this.type).subscribe(newValue => (this.type = newValue as GRID_TYPES)),
      WindowResizeService.registerProp(this.vertical).subscribe(newValue => {
        this.vertical = newValue as FLEX_JUSTIFY_OPTIONS;
        this.inlineStyle = {
          ...this.inlineStyle,
          '--align-items': this.vertical,
        };
      }),
      WindowResizeService.registerProp(this.horizontal).subscribe(newValue => {
        this.horizontal = newValue as FLEX_JUSTIFY_OPTIONS;
        this.inlineStyle = {
          ...this.inlineStyle,
          '--justify-content': this.horizontal,
        };
      }),
      WindowResizeService.registerProp(this.flexShrink).subscribe(newValue => (this.flexShrink = newValue as string)),
      WindowResizeService.registerProp(this.flexGrow).subscribe(newValue => (this.flexGrow = newValue as string)),
      WindowResizeService.registerProp(this.columns).subscribe(newValue => {
        this.columns = newValue as string;
        this.inlineStyle = {
          ...this.inlineStyle,
          '--columns': newValue as string,
        };
      }),
      WindowResizeService.registerProp(this.columnGap).subscribe(newValue => {
        this.columnGap = newValue as string;
        this.inlineStyle = {
          ...this.inlineStyle,
          '--column-gap': newValue as string,
        };
      }),
      WindowResizeService.registerProp(this.rowGap).subscribe(newValue => {
        this.rowGap = newValue as string;
        this.inlineStyle = {
          ...this.inlineStyle,
          '--row-gap': newValue as string,
        };
      }),
      WindowResizeService.registerProp(this.classes).subscribe(newValue => (this.classString = newValue as string)),
    ]);
  }

  render() {
    return (
      <Host class={this.classString} style={this.inlineStyle}>
        <slot />
      </Host>
    );
  }
}
